import { SyntheticEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DatePicker, DateTools, Input, Patterns, Select, RadioList, Utils } from '@treezor/design-system';

import { UserTools } from '@/utils/tools/UserTools';

import { useMailInput } from '@/features/user/hooks/createUser/useMailInput';
import { Contact } from '@/features/user/components/CreateUser/Contact';
import { SirenInput } from '@/features/user/components/CreateUser/SirenInput';
import { useFieldOptions } from '@/features/user/hooks/createUser/useFieldOptions';
import { AdditonalDataLayout } from '@/features/user/components/CreateUser/layout/AddtionalData';
import { CreateUserFooter } from '@/features/user/components/CreateUser/layout/CreateUserFooter';
import { CreateUserEmptyComponent } from '@/features/user/components/CreateUser/layout/CreateUserEmptyComponent';
import { TCountryCode } from '@/features/user/store/user.model';
import {
  ContentLayout,
  TwoColumns,
  CustomColumns,
  Section,
  SectionInformation,
} from '@/features/user/components/CreateUser/layout/ContentLayout';
import { useCreateSelfEmployedUser } from '@/features/user/hooks/createUser/useCreateSelfEmployedUser';
import { ICreateUserPartProps } from '@/features/user/store/createUser.types';

export function CreateUserSelfUserInformation({ stepsLength }: ICreateUserPartProps) {
  const { t } = useTranslation();

  const { countryCodeOptions, titleOptions, legalSectorTypeOptions, persoAndBusinessAddressOptions } =
    useFieldOptions();

  const {
    distributionCountry,
    handleSubmit,
    watches,
    user,
    updateUser,
    register,
    touchedFields,
    inputMaxLength,
    handleInputChange,
    handleValueChange,
    handleDateChange,
    hasInvalidInputLength,
    hasValidField,
    getValues,
    language,
    isSameAsBusinessAddress,
    handleIsSameAsBusinessAddressChange,
    handlePrefillData,
  } = useCreateSelfEmployedUser({
    email: '',
    title: undefined,
    lastname: '',
    firstname: '',
    middleName1: '',
    middleName2: '',
    distributionCountry: undefined,
    legalRegistrationNumber: '',
    legalName: '',
    legalRegistrationDate: '',
    legalTvaNumber: '',
    legalSectorType: 'NAF',
    legalSector: '',
    country: undefined,
    address1: '',
    address2: '',
    address3: '',
    postcode: '',
    city: '',
    secondaryCountry: undefined,
    secondaryAddress1: '',
    secondaryAddress2: '',
    secondaryAddress3: '',
    phone: '',
    mobile: '',
  });

  const hasDistributionCountry = distributionCountry?.length > 0;

  const emailWatch = watches['email'];
  const lastNameWatch = watches['lastname'];
  const firstNameWatch = watches['firstname'];
  const distributionCountryWatch = watches['distributionCountry'];
  const legalRegistrationNumberWatch = watches['legalRegistrationNumber'];
  const legalRegistrationDateWatch = watches['legalRegistrationDate'];
  const selectedlegalRegistrationDate = DateTools.localStringToDate(legalRegistrationDateWatch, language);
  const legalSectorTypeWatch = watches['legalSectorType'];
  const legalSectorWatch = watches['legalSector'];
  const countryWatch = watches['country'];
  const address1Watch = watches['address1'];
  const address2Watch = watches['address2'];
  const address3Watch = watches['address3'];
  const secondaryCountryWatch = watches['secondaryCountry'];
  const secondaryAddress1Watch = watches['secondaryAddress1'];
  const secondaryAddress2Watch = watches['secondaryAddress2'];
  const secondaryAddress3Watch = watches['secondaryAddress3'];
  const phoneWatch = watches['phone'];
  const mobileWatch = watches['mobile'];

  const {
    iconStatus,
    error: emailError,
    handleAsyncBlur: handleEmailBlur,
  } = useMailInput({ initialValue: emailWatch });
  const hasEnteredInformation = hasValidField([
    'legalRegistrationNumber',
    'legalName',
    'legalSector',
    'legalTvaNumber',
  ]);

  const isFormDisabled = useMemo(
    () =>
      !emailWatch ||
      !!emailError ||
      !firstNameWatch ||
      !lastNameWatch ||
      (hasDistributionCountry && !distributionCountryWatch) ||
      !legalRegistrationNumberWatch ||
      !UserTools.validateIntLegalNumber(legalRegistrationNumberWatch) ||
      (!!legalSectorWatch && !Utils.validateLegalSector(legalSectorWatch, legalSectorTypeWatch)) ||
      hasInvalidInputLength(['address1', 'address2', 'address3']) ||
      hasInvalidInputLength(['secondaryAddress1', 'secondaryAddress2', 'secondaryAddress3']) ||
      (!!mobileWatch && !Patterns.phone.test(mobileWatch)) ||
      (!!phoneWatch && !Patterns.phone.test(phoneWatch)),
    [
      emailWatch,
      emailError,
      firstNameWatch,
      lastNameWatch,
      hasDistributionCountry,
      distributionCountryWatch,
      legalRegistrationNumberWatch,
      legalSectorTypeWatch,
      legalSectorWatch,
      hasInvalidInputLength,
      mobileWatch,
      phoneWatch,
    ],
  );

  const onChangeIsSameAsBusinessAddress = useCallback(
    (value: 'yes' | 'no') => {
      handleIsSameAsBusinessAddressChange(value);
      if (value === 'no') {
        handleValueChange('', 'secondaryCountry');
        handleValueChange('', 'secondaryAddress1');
        handleValueChange('', 'secondaryAddress2');
        handleValueChange('', 'secondaryAddress3');
        handleValueChange('', 'secondaryPostcode');
        handleValueChange('', 'secondaryCity');
      }
    },
    [handleIsSameAsBusinessAddressChange, handleValueChange],
  );

  const beforeSubmit = useCallback(() => {
    if (isSameAsBusinessAddress) {
      const { country, address1, address2, address3, postcode, city } = getValues();
      handleValueChange(country, 'secondaryCountry');
      handleValueChange(address1, 'secondaryAddress1');
      handleValueChange(address2, 'secondaryAddress2');
      handleValueChange(address3, 'secondaryAddress3');
      handleValueChange(postcode, 'secondaryPostcode');
      handleValueChange(city, 'secondaryCity');
    }
    handleSubmit();
  }, [getValues, handleSubmit, handleValueChange, isSameAsBusinessAddress]);

  return (
    <div className="flex flex-col gap-7">
      <ContentLayout title={t('pages.dashboard.createUser.form.blocks.mainInformation')}>
        <div className="flex flex-col gap-7">
          <Section>
            <Input
              type="text"
              icon={{ left: 'mail', right: iconStatus }}
              label={t('pages.dashboard.createUser.form.email')}
              required
              {...register('email', { pattern: Patterns.email })}
              error={emailError}
              onBlur={handleEmailBlur}
            />

            <CustomColumns columns={[1, 2, 2]}>
              <Select
                label={t('pages.dashboard.createUser.form.title')}
                options={titleOptions}
                defaultValue={user?.title}
                onChange={(value: string) => handleValueChange(value, 'title')}
                showDelete
              />

              <Input
                type="text"
                label={t('pages.dashboard.createUser.form.lastname')}
                required
                {...register('lastname')}
                onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleInputChange(event, 'lastname')
                }
              />
              <Input
                type="text"
                label={t('pages.dashboard.createUser.form.firstname')}
                required
                {...register('firstname')}
                onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleInputChange(event, 'firstname')
                }
              />
            </CustomColumns>
            <AdditonalDataLayout
              shouldShow={hasValidField(['middleName1', 'middleName2'])}
              label={t('pages.dashboard.createUser.form.actions.addMiddleNames')}
            >
              <TwoColumns>
                <Input
                  type="text"
                  label={t('pages.dashboard.createUser.form.middleName')}
                  {...register('middleName1')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'middleName1')
                  }
                />
                <Input
                  type="text"
                  label={t('pages.dashboard.createUser.form.middleName')}
                  {...register('middleName2')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'middleName2')
                  }
                />
              </TwoColumns>
            </AdditonalDataLayout>

            {hasDistributionCountry && (
              <Select
                label={t('pages.currentProfiles.profile.informations.distributionCountry')}
                required
                withSearch
                options={countryCodeOptions.filter(({ value }) => distributionCountry.includes(value))}
                defaultValue={user?.distributionCountry}
                noOptionsComponent={<CreateUserEmptyComponent />}
                onChange={(value: string) => handleValueChange(value, 'distributionCountry')}
              />
            )}
          </Section>

          <Section title={t('pages.dashboard.createUser.selfEmployed.sections.legalInformation')}>
            <SirenInput
              required
              value={legalRegistrationNumberWatch}
              hasEntered={hasEnteredInformation}
              label={t('pages.dashboard.createUser.form.sirenLegalRegistrationNumber')}
              handlePrefillData={handlePrefillData}
              {...register('legalRegistrationNumber')}
            >
              <Input
                {...register('legalName')}
                type="text"
                label={t('pages.dashboard.createUser.form.legalName')}
                onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleInputChange(event, 'legalName')
                }
              />
              <CustomColumns columns={[1, 2]}>
                <DatePicker
                  selectedDate={selectedlegalRegistrationDate}
                  label={t('pages.dashboard.createUser.form.legalRegistrationDate')}
                  todayButtonLabel={t('pages.dashboard.createUser.form.actions.today')}
                  clearButtonLabel={t('pages.dashboard.createUser.form.actions.clear')}
                  onChangeDate={(value) => handleDateChange(value, 'legalRegistrationDate')}
                  language={language}
                />
                <Input
                  type="text"
                  {...register('legalTvaNumber')}
                  label={t('pages.dashboard.createUser.form.legalTvaNumber')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    handleInputChange(event, 'legalTvaNumber');
                  }}
                />
              </CustomColumns>
              <Section title={t('pages.dashboard.createUser.business.sections.companyActivities')}>
                <CustomColumns columns={[1, 1]}>
                  <RadioList
                    label={t('pages.dashboard.createUser.form.legalSectorType')}
                    options={legalSectorTypeOptions}
                    value={legalSectorTypeWatch}
                    onChange={(value: string) => handleValueChange(value, 'legalSectorType')}
                    className="pt-2 gap-3"
                    isRequired
                  />
                  <Input
                    type="text"
                    label={t('pages.dashboard.createUser.form.legalSectorNafApe')}
                    {...register('legalSector')}
                    error={
                      legalSectorWatch === '' || Utils.validateLegalSector(legalSectorWatch, legalSectorTypeWatch)
                        ? undefined
                        : legalSectorTypeWatch === 'NACE'
                          ? t('pages.dashboard.createUser.form.rules.incorrectNaceLegalSector')
                          : t('pages.dashboard.createUser.form.rules.incorrectLegalSector')
                    }
                    onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      handleInputChange(event, 'legalSector')
                    }
                  />
                </CustomColumns>
              </Section>
            </SirenInput>
          </Section>
        </div>
      </ContentLayout>

      <ContentLayout title={t('pages.dashboard.createUser.form.blocks.address')}>
        <Section title={t('pages.dashboard.createUser.selfEmployed.sections.professionalAddress.title')}>
          <SectionInformation
            title={t('pages.dashboard.createUser.selfEmployed.sections.professionalAddress.infoTitle')}
            content={t('pages.dashboard.createUser.selfEmployed.sections.professionalAddress.infoContent')}
          />

          <Select
            label={t('pages.currentProfiles.profile.contact.country')}
            options={countryCodeOptions}
            withSearch
            defaultValue={countryWatch}
            onChange={(value: string) => {
              handleValueChange(value, 'country');
              updateUser({ ...user, country: value as TCountryCode });
            }}
            noOptionsComponent={<CreateUserEmptyComponent />}
            showDelete
          />

          <CustomColumns columns={[1, 1]}>
            <Input
              value={address1Watch}
              type="text"
              label={t('pages.dashboard.createUser.form.address1')}
              {...register('address1')}
              onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                handleInputChange(event, 'address1')
              }
              {...inputMaxLength(address1Watch, 35)}
            />

            <Input
              value={address2Watch}
              type="text"
              label={t('pages.dashboard.createUser.form.address2')}
              {...register('address2')}
              onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                handleInputChange(event, 'address2')
              }
              {...inputMaxLength(address2Watch, 35)}
            />
          </CustomColumns>

          <Input
            value={address3Watch}
            type="text"
            label={t('pages.dashboard.createUser.form.address3')}
            {...register('address3')}
            onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleInputChange(event, 'address3')
            }
            {...inputMaxLength(address3Watch, 35)}
          />

          <CustomColumns columns={[1, 1]}>
            <Input
              type="text"
              label={t('pages.currentProfiles.profile.contact.postcode')}
              {...register('postcode')}
              onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                handleInputChange(event, 'postcode')
              }
            />

            <Input
              type="text"
              label={t('pages.currentProfiles.profile.contact.city')}
              {...register('city')}
              onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                handleInputChange(event, 'city')
              }
            />
          </CustomColumns>
        </Section>

        <Section title={t('pages.dashboard.createUser.selfEmployed.sections.personnalAddress')}>
          <RadioList
            name="isSameAsBusinessAddress"
            options={persoAndBusinessAddressOptions}
            value={isSameAsBusinessAddress ? 'yes' : 'no'}
            onChange={onChangeIsSameAsBusinessAddress}
            className="pt-2 gap-3"
            direction="row"
          />

          {!isSameAsBusinessAddress && (
            <>
              <Select
                label={t('pages.currentProfiles.profile.contact.country')}
                options={countryCodeOptions}
                withSearch
                defaultValue={secondaryCountryWatch}
                onChange={(value: string) => handleValueChange(value, 'secondaryCountry')}
                noOptionsComponent={<CreateUserEmptyComponent />}
                showDelete
              />

              <CustomColumns columns={[1, 1]}>
                <Input
                  value={secondaryAddress1Watch}
                  type="text"
                  label={t('pages.dashboard.createUser.form.address1')}
                  {...register('secondaryAddress1')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'secondaryAddress1')
                  }
                  {...inputMaxLength(secondaryAddress1Watch, 35)}
                />

                <Input
                  value={secondaryAddress2Watch}
                  type="text"
                  label={t('pages.dashboard.createUser.form.address2')}
                  {...register('secondaryAddress2')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'secondaryAddress2')
                  }
                  {...inputMaxLength(secondaryAddress2Watch, 35)}
                />
              </CustomColumns>

              <Input
                value={secondaryAddress3Watch}
                type="text"
                label={t('pages.dashboard.createUser.form.address3')}
                {...register('secondaryAddress3')}
                onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleInputChange(event, 'secondaryAddress3')
                }
                {...inputMaxLength(secondaryAddress3Watch, 35)}
              />

              <CustomColumns columns={[1, 1]}>
                <Input
                  type="text"
                  label={t('pages.currentProfiles.profile.contact.postcode')}
                  {...register('secondaryPostcode')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'secondaryPostcode')
                  }
                />

                <Input
                  type="text"
                  label={t('pages.currentProfiles.profile.contact.city')}
                  {...register('secondaryCity')}
                  onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                    handleInputChange(event, 'secondaryCity')
                  }
                />
              </CustomColumns>
            </>
          )}
        </Section>
      </ContentLayout>

      <ContentLayout title={t('pages.dashboard.createUser.form.blocks.contact')} hasDivider={false}>
        <TwoColumns>
          <Contact
            label={t('pages.dashboard.createUser.form.phone')}
            value={phoneWatch}
            initialValue={user?.phone}
            initialCountry={!touchedFields?.['phone'] && !phoneWatch && distributionCountryWatch}
            handleValueChange={handleValueChange}
            {...register('phone')}
          />
          <Contact
            label={t('pages.dashboard.createUser.form.mobile')}
            value={mobileWatch}
            initialValue={user?.mobile}
            initialCountry={!touchedFields?.['mobile'] && !mobileWatch && distributionCountryWatch}
            handleValueChange={handleValueChange}
            {...register('mobile')}
          />
        </TwoColumns>
      </ContentLayout>
      <CreateUserFooter isNextDisabled={isFormDisabled} onNext={beforeSubmit} stepsLength={stepsLength} />
    </div>
  );
}
